import { FormEvent, useEffect, useId, useState } from 'react';
import { PublicDeliveryInfo } from '../types/store';

type PublicDeliveryModalProps = {
  isOpen: boolean;
  deliveryInfo: PublicDeliveryInfo;
  onClose: () => void;
  onSubmit: (delivery: PublicDeliveryInfo) => void;
  isLocked?: boolean;
};

export default function PublicDeliveryModal({
  isOpen,
  deliveryInfo,
  onClose,
  onSubmit,
  isLocked = false,
}: PublicDeliveryModalProps) {
  const idPrefix = useId();
  const [deliveryDraft, setDeliveryDraft] = useState<PublicDeliveryInfo>(deliveryInfo);

  useEffect(() => {
    setDeliveryDraft(deliveryInfo);
  }, [deliveryInfo]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const address = deliveryDraft.address.trim();
    const phone = deliveryDraft.phone.replace(/\D/g, '');
    const reference = deliveryDraft.reference.trim();

    if (!address || phone.length < 9) {
      return;
    }

    onSubmit({ address, phone, reference });
    onClose();
  };

  return (
    <div
      className={`modal-overlay ${isLocked ? 'modal-overlay-locked' : ''}`}
      onClick={isLocked ? undefined : onClose}
      role="presentation"
    >
      <div
        className="modal-content modal-content-sm"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${idPrefix}-address-modal-title`}
      >
        <div className="modal-header">
          <h3 id={`${idPrefix}-address-modal-title`}>
            {isLocked ? 'Ingresa tu direccion de entrega' : 'Editar direccion de entrega'}
          </h3>
          {!isLocked ? (
            <button type="button" className="modal-close" onClick={onClose}>
              x
            </button>
          ) : null}
        </div>

        {isLocked ? (
          <p className="modal-required-note">
            Para continuar, ingresa la direccion donde recibiras tus pedidos.
          </p>
        ) : null}

        <form className="medicine-form" onSubmit={handleSubmit}>
          <div className="field-group field-row-full">
            <label htmlFor={`${idPrefix}-delivery-address`}>Direccion</label>
            <input
              id={`${idPrefix}-delivery-address`}
              type="text"
              value={deliveryDraft.address}
              onChange={(event) =>
                setDeliveryDraft((prev) => ({ ...prev, address: event.target.value }))
              }
              placeholder="Ingresa tu direccion de entrega"
              autoFocus
              required
            />
          </div>

          <div className="field-group field-row-full">
            <label htmlFor={`${idPrefix}-delivery-phone`}>Telefono</label>
            <input
              id={`${idPrefix}-delivery-phone`}
              type="tel"
              inputMode="numeric"
              value={deliveryDraft.phone}
              onChange={(event) =>
                setDeliveryDraft((prev) => ({
                  ...prev,
                  phone: event.target.value.replace(/\D/g, ''),
                }))
              }
              placeholder="987654321"
              maxLength={9}
              required
            />
          </div>

          <div className="field-group field-row-full">
            <label htmlFor={`${idPrefix}-delivery-reference`}>Referencia</label>
            <textarea
              id={`${idPrefix}-delivery-reference`}
              className="textarea-field"
              rows={3}
              value={deliveryDraft.reference}
              onChange={(event) =>
                setDeliveryDraft((prev) => ({ ...prev, reference: event.target.value }))
              }
              placeholder="Ej. Al costado de la tienda, piso 3, timbre azul"
            />
          </div>

          <div className="medicine-form-actions">
            {!isLocked ? (
              <button type="button" className="secondary-button" onClick={onClose}>
                Cancelar
              </button>
            ) : null}
            <button type="submit">{isLocked ? 'Continuar' : 'Guardar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
